import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, RefreshCw, MonitorPlay, Radio, Link2, KeyRound, ExternalLink, Loader2, FolderSync, Bookmark, Scissors, PlugZap, BookOpen } from "lucide-react";
import { api, formatApiError, getToken, API, PUBLIC_ORIGIN } from "@/lib/api";
import { CONNECT_PLATFORMS, PlatformConnectDialog, LiveSessions } from "@/components/PlatformConnect";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { QuickClipAccess } from "@/components/QuickClipAccess";
import { ModsLeaderboard } from "@/components/ModsLeaderboard";
import { LiveSetupWizard } from "@/components/LiveSetupWizard";
import { OverlayHolidayToggle } from "@/components/holiday/OverlayHolidayToggle";

const Card = ({ icon: Icon, title, desc, children, testid }) => (
  <div className="cx-card rounded-2xl p-6 flex flex-col" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl grid place-items-center border border-[#2f7bff]/40 bg-[#2f7bff]/10 text-[#3ad7ff]"><Icon className="w-5 h-5" /></div>
      <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
    </div>
    <p className="text-sm text-slate-400 mt-3 leading-relaxed">{desc}</p>
    <div className="mt-5 flex-1 flex flex-col justify-end">{children}</div>
  </div>
);

const CopyRow = ({ value, testid, t }) => (
  <div className="flex gap-2">
    <input readOnly value={value} className="flex-1 min-w-0 rounded-lg bg-[#070912] border border-white/10 px-3 py-2.5 text-xs font-mono text-[#3ad7ff]" data-testid={`${testid}-value`} />
    <button data-testid={`${testid}-copy`} onClick={() => { navigator.clipboard.writeText(value); toast.success(t("int.copied")); }} className="cx-ghost rounded-lg px-3"><Copy className="w-4 h-4" /></button>
  </div>
);

export const ImportLink = ({ onDone, compact = false }) => {
  const { t, lang } = useI18n();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/videos/import", { url, analyze: { lang } });
      toast.success(t("up.import_started"));
      setUrl("");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="flex gap-2" data-testid="import-link-form">
      <input data-testid="import-url-input" value={url} onChange={(e) => setUrl(e.target.value)} required placeholder={compact ? t("up.or_link") : "https://twitch.tv/videos/..."} className="flex-1 min-w-0 rounded-lg bg-[#070912] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#2f7bff]" />
      <button data-testid="import-url-btn" disabled={busy} className="cx-btn rounded-lg px-4 text-sm font-semibold flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}<span className="hidden sm:inline">{t("up.import")}</span></button>
    </form>
  );
};

const Bookmarklet = ({ label }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.setAttribute("href", `javascript:(function(){window.open('${PUBLIC_ORIGIN}/dashboard?import='+encodeURIComponent(location.href),'_blank')})()`);
  }, []);
  return (
    <a ref={ref} data-testid="bookmarklet-btn" onClick={(e) => e.preventDefault()} draggable
      className="cx-btn rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 cursor-grab"><Bookmark className="w-4 h-4" /> {label}</a>
  );
};

const PlatformButtons = ({ t, onPick }) => (
  <section className="mt-10" data-testid="platform-buttons">
    <h2 className="font-display text-xl sm:text-2xl font-bold text-white">{t("con.title")}</h2>
    <p className="text-sm text-slate-400 mt-1 mb-5">{t("con.sub")}</p>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3">
      {CONNECT_PLATFORMS.map((p) => (
        <button key={p.id} data-testid={`connect-${p.id}`} onClick={() => onPick(p)}
          className="cx-card cx-card-hover rounded-xl px-4 py-4 flex items-center gap-3 text-left">
          <span className="w-3 h-3 rounded-full shrink-0 shadow-[0_0_12px_currentColor]" style={{ background: p.color, color: p.color }} />
          <span className="min-w-0">
            <span className="block font-display font-semibold text-white truncate">{p.name}</span>
            <span className="block text-[10px] font-mono uppercase text-slate-500">{p.live ? t("con.tag_live") : t("con.tag_overlay")}</span>
          </span>
        </button>
      ))}
    </div>
  </section>
);

const Guide = ({ t }) => (
  <section className="cx-card rounded-2xl p-6 mt-6" data-testid="integrations-guide">
    <div className="flex items-center gap-2 text-white font-display font-semibold text-lg"><BookOpen className="w-5 h-5 text-[#3ad7ff]" />{t("int.guide_t")}</div>
    <ol className="mt-4 space-y-2 text-sm text-slate-300 list-decimal list-inside">
      {[1, 2, 3, 4].map((n) => <li key={n} data-testid={`guide-step-${n}`}>{t(`int.guide_${n}`)}</li>)}
    </ol>
    <div className="grid md:grid-cols-3 gap-3 mt-5">
      {["tiktok", "obs", "streamlabs"].map((k) => <div key={k} className="rounded-xl bg-white/[0.03] border border-white/5 p-4 text-xs text-slate-400 leading-relaxed" data-testid={`guide-sw-${k}`}>{t(`int.sw_${k}`)}</div>)}
    </div>
  </section>
);

export default function Integrations() {
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [picked, setPicked] = useState(null);
  const [liveKey, setLiveKey] = useState(0);
  const { user } = useAuth();
  useEffect(() => { api.get("/integrations").then((r) => setInfo({ ...r.data, demo_role: user?.role === "demo" })).catch(() => setInfo({ demo: true })); }, [user?.role]);
  const overlay = info?.api_key ? `${PUBLIC_ORIGIN}/overlay/${info.api_key}` : "";
  const panel = info?.api_key ? `${PUBLIC_ORIGIN}/panel/${info.api_key}` : "";
  const test = async () => {
    const r = await fetch(`${API}/integrations/ping`, { headers: { "X-API-Key": info.api_key }, credentials: "omit" }).catch(() => null);
    const d = r && await r.json().catch(() => ({}));
    if (r?.ok) toast.success(`${t("int.test_ok")} ${d.workspace}`); else toast.error(d?.detail || "Erreur");
  };

  const rotate = async () => {
    try { const { data } = await api.post("/integrations/rotate"); setInfo({ ...info, api_key: data.api_key }); toast.success(t("int.rotated")); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 2xl:px-14 py-6 sm:py-10">
      <div className="cx-label">{t("int.label")}</div>
      <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl mt-3"><span className="cx-chrome">{t("int.title1")}</span><span className="cx-text-grad">{t("int.title2")}</span></h1>
      <p className="text-slate-400 mt-3 max-w-3xl text-sm sm:text-base">{t("int.sub")}</p>
      {info?.restricted && <div className="mt-6 rounded-xl border border-[#2f7bff]/30 bg-[#2f7bff]/10 text-slate-200 text-sm px-5 py-4" data-testid="integrations-restricted-note">{t("int.restricted")}</div>}
      {info?.demo && <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-200 text-sm px-5 py-4" data-testid="integrations-demo-note">{t("int.demo")}</div>}
      {info?.api_key && <LiveSetupWizard overlay={overlay} panel={panel} refreshKey={liveKey} />}

      {info?.api_key && (
        <div className="cx-card cx-brackets rounded-2xl p-6 mt-8" data-testid="api-key-panel">
          <div className="cx-label flex items-center gap-2"><KeyRound className="w-3.5 h-3.5" /> {t("int.key")}</div>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <div className="flex-1"><CopyRow value={info.api_key} testid="api-key" t={t} /></div>
            <button data-testid="api-key-test-btn" onClick={test} className="cx-ghost rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2"><PlugZap className="w-4 h-4" /> {t("int.test")}</button>
            {!info.demo_role && <button data-testid="api-key-rotate-btn" onClick={rotate} className="cx-ghost rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2"><RefreshCw className="w-4 h-4" /> {t("int.rotate")}</button>}
          </div>
        </div>
      )}

      <Guide t={t} />
      <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-6 mt-6">
        <Card icon={Scissors} title={t("int.panel_t")} desc={t("int.panel_d")} testid="integration-panel">
          {panel ? (
            <div className="space-y-2">
              <a href={panel} target="_blank" rel="noreferrer" data-testid="panel-open-link" className="cx-btn rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2"><Scissors className="w-4 h-4" /> {t("int.panel_open")}</a>
              <QuickClipAccess panelUrl={panel} />
            </div>
          ) : <div className="text-xs text-slate-500">—</div>}
        </Card>
        <Card icon={MonitorPlay} title={t("int.obs_t")} desc={t("int.obs_d")} testid="integration-obs">
          <ol className="text-xs text-slate-300 space-y-2 mb-5 list-decimal list-inside">
            <li>{t("int.obs_s1")}</li><li>{t("int.obs_s2")}</li><li>{t("int.obs_s3")}</li>
          </ol>
          <a data-testid="obs-script-download-btn" href={info?.api_key ? `${API}/integrations/script/obs?token=${getToken()}` : undefined}
            className={`cx-btn rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 ${info?.api_key ? "" : "opacity-40 pointer-events-none"}`}><Download className="w-4 h-4" /> {t("int.obs_dl")}</a>
        </Card>
        <Card icon={Radio} title={t("int.overlay_t")} desc={t("int.overlay_d")} testid="integration-overlay">
          {overlay ? (
            <div className="space-y-2">
              <div className="text-[11px] text-slate-400">{t("int.ov_invisible")}</div>
              <CopyRow value={overlay} testid="overlay-url" t={t} />
              <div className="text-[11px] text-slate-400 pt-1">{t("int.ov_alert")}</div>
              <CopyRow value={`${overlay}?alert=1`} testid="overlay-alert-url" t={t} />
              <div className="text-[11px] text-slate-400 pt-1">{t("int.ov_mod")}</div>
              <CopyRow value={`${overlay}?mod=1`} testid="overlay-mod-url" t={t} />
              <a href={`${overlay}?mod=1&demo=1`} target="_blank" rel="noreferrer" data-testid="overlay-mod-preview-link" className="text-[11px] text-[#ff9df0] hover:underline">{t("int.ov_mod_preview")}</a>
              <p className="text-[11px] text-[#22e3a1]/80">{t("int.ov_note")}</p>
              {!info.demo_role && <OverlayHolidayToggle overlay={overlay} />}
              <a href={`${overlay}?alert=1`} target="_blank" rel="noreferrer" data-testid="overlay-preview-link" className="cx-ghost rounded-full py-2 text-xs flex items-center justify-center gap-2"><ExternalLink className="w-3.5 h-3.5" /> {t("int.open")}</a>
            </div>
          ) : <div className="text-xs text-slate-500">—</div>}
        </Card>
        <Card icon={FolderSync} title={t("int.watcher_t")} desc={t("int.watcher_d")} testid="integration-watcher">
          <a data-testid="watcher-download-btn" href={info?.api_key ? `${API}/integrations/script/watcher?token=${getToken()}` : undefined}
            className={`cx-btn rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 ${info?.api_key ? "" : "opacity-40 pointer-events-none"}`}><Download className="w-4 h-4" /> {t("int.dl_watcher")}</a>
        </Card>
        <Card icon={Link2} title={t("int.import_t")} desc={t("int.import_d")} testid="integration-import">
          <div className="space-y-3">
            <ImportLink />
            <div className="text-xs text-slate-400">{t("int.bookmarklet_d")}</div>
            <Bookmarklet label={t("int.bookmarklet_btn")} />
          </div>
        </Card>
      </div>
      <PlatformButtons t={t} onPick={setPicked} />
      <LiveSessions refreshKey={liveKey} />
      {info?.api_key && <ModsLeaderboard refreshKey={liveKey} />}
      <PlatformConnectDialog key={picked?.id || "none"} p={picked} overlay={overlay} panel={panel} onClose={() => setPicked(null)} onStarted={() => setLiveKey((k) => k + 1)} />
    </div>
  );
}
